import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { SorareUser } from '@sorare/core/src/components/user/SorareUser';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';

import { TokenOwner } from '#src/components/TokenPreview/TokenOwner/index.tsx';
import { AuctionBidsInfo } from '#src/components/auction/Auction/AuctionDetails/AuctionBidsInfo/index.tsx';
import { CardBonus } from '#src/components/card/CardBonus/index.tsx';
import { CollectionPrediction } from '#src/components/card/CollectionPrediction/index.tsx';
import { DiscountedPrice } from '#src/components/marketCredit/DiscountedPrice/index.tsx';
import { useGetAuctionDetails } from '#src/hooks/offers/useGetAuctionDetails.ts';
import { useGetTokenSingleSaleDetails } from '#src/hooks/offers/useGetTokenSingleSaleDetails.ts';
import { getSteppedPrimaryOffer } from '#src/hooks/primaryBuy/steppedPrimaryOffer.ts';
import { useIsPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';

import type { PlayerDrawerTokenDetails_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  anyCard: PlayerDrawerTokenDetails_anyCard;
  center?: boolean;
};
const Root = styled(Vertical)`
  flex-grow: 0;
  max-width: calc(100% - calc(7 * var(--unit)));
`;
const Line = styled(Horizontal)`
  align-items: center;
  flex-wrap: wrap;
`;

export const Details = ({ anyCard, center }: Props) => {
  const auction = useGetAuctionDetails(anyCard.latestEnglishAuction);
  const offer = useGetTokenSingleSaleDetails(anyCard);
  const primaryBuy = useIsPrimaryBuyLive(anyCard);
  const { toMonetaryAmount } = useMonetaryAmount();
  const { serialNumber, supply } = anyCard;

  const price = (() => {
    if (primaryBuy) return toMonetaryAmount(primaryBuy.price);
    if (offer) return toMonetaryAmount(offer.price);
    if (auction) return toMonetaryAmount(auction.price);
    if (anyCard?.tokenOwner?.amounts)
      return toMonetaryAmount(anyCard.tokenOwner.amounts);
    return null;
  })();

  const owner = (() => {
    if (primaryBuy) return <SorareUser />;
    if (auction) return null;
    return <TokenOwner card={anyCard} withAvatar />;
  })();

  const stepped = getSteppedPrimaryOffer(anyCard.livePrimaryOffer);
  const showLock = !!primaryBuy && stepped.isCampaign;

  const timeLeft = offer && <TimeLeft time={offer.endDate} />;

  const bids = (() => {
    if (auction && anyCard?.latestEnglishAuction)
      return <AuctionBidsInfo auction={anyCard.latestEnglishAuction} />;
    return null;
  })();

  return (
    <Root gap={center ? 1 : 0.5} center={center}>
      <Line>
        <CardBonus card={anyCard} withTransferMalus={!!offer} />
        <CollectionPrediction card={anyCard} />
      </Line>
      {!!price && (
        <Horizontal gap={0.5} center>
          {!!showLock && (
            <FontAwesomeIcon icon={iconLock} size="sm" color={stepped.color} />
          )}
          <DiscountedPrice
            canDiscount={!!primaryBuy}
            price={price}
            sport={anyCard.sport}
          />
        </Horizontal>
      )}
      <LabelS color="var(--c-nd-600)" as="span">
        <Line gap={0.5}>
          {serialNumber}/{supply}
          {owner ? <> • {owner}</> : ''}
          {timeLeft ? <> • {timeLeft}</> : ''}
          {bids ? <> • {bids}</> : ''}
        </Line>
      </LabelS>
    </Root>
  );
};

Details.fragments = {
  anyCard: gql`
    fragment PlayerDrawerTokenDetails_anyCard on AnyCardInterface {
      slug
      serialNumber
      supply
      sport
      tokenOwner {
        id
        amounts {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      latestEnglishAuction {
        id
        ...useGetAuctionDetails_auction
        ...AuctionBidsInfo_auction
      }
      livePrimaryOffer {
        id
        ...SteppedPrimaryOffer_primaryOffer
      }
      ...CardBonus_anyCard
      ...useGetTokenSingleSaleDetails_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...TokenOwner_anyCard
      ...CollectionPrediction_anyCard
    }
    ${CardBonus.fragments.anyCard}
    ${useGetAuctionDetails.fragments.auction}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${TokenOwner.fragments.anyCard}
    ${AuctionBidsInfo.fragments.auction}
    ${CollectionPrediction.fragments.anyCard}
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${getSteppedPrimaryOffer.fragments.primaryOffer}
  ` as TypedDocumentNode<PlayerDrawerTokenDetails_anyCard>,
};
